import React, { useState } from "react";
import { useEffect as _useEffectChk } from "react";
import { markChecklist as _markChk } from "../lib/checklist";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Sun, Sunset, Moon, CheckCircle2, LogOut, History, CalendarDays, Utensils } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ExportButton } from "../components/ExportButton";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useOrariTurni, orarioLabel } from "../lib/orari";

const TURNI = [
  { key: "mattina", label: "Mattina", icon: Sun, color: "#16a34a", bg: "#dcfce7" },
  { key: "pomeriggio", label: "Pomeriggio", icon: Sunset, color: "#2563eb", bg: "#dbeafe" },
  { key: "notte", label: "Notte", icon: Moon, color: "#7c3aed", bg: "#ede9fe" },
  { key: "cucina", label: "Cucina", icon: Utensils, color: "#0891b2", bg: "#cffafe" },
];
// Un turno combinato copre più fasce: N/M (not_mat) = notte+mattina, M/P (mat_pom) = mattina+pomeriggio.
const TURNO_FASCE = { mattina: ["mattina"], pomeriggio: ["pomeriggio"], notte: ["notte"], sera: ["notte"], not_mat: ["notte", "mattina"], mat_pom: ["mattina", "pomeriggio"], cucina: ["cucina"] };
const fasceOf = (turno) => TURNO_FASCE[turno] || [turno];
const cardMatch = (turno, key) => fasceOf(turno).includes(key);
const MANAGER = ["super_admin", "admin", "responsabile", "segreteria"];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const nowHM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
const hm2min = (s) => { const [h, m] = String(s).split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const LIV = { puntuale: ["Puntuale", "bg-emerald-100 text-emerald-700"], lieve: ["Ritardo lieve", "bg-amber-100 text-amber-700"], ritardo: ["In ritardo", "bg-red-100 text-red-700"] };

export default function Presenze() {
  const qc = useQueryClient();
  const { user, selectedStructure, structures } = useAuth();
  const { activeOperatore, opName } = useFirma();
  const [tab, setTab] = useState("oggi");
  const isManager = MANAGER.includes(user?.role);
  const onlySelf = !!activeOperatore && !isManager;
  const today = ymd(new Date());
  const [selDate, setSelDate] = useState(today);
  const isToday = selDate === today;
  const structForOp = activeOperatore?.structure_id || (selectedStructure !== "all" ? selectedStructure : structures?.[0]?.id);
  const orari = useOrariTurni(structForOp);
  const shiftStart = { mattina: hm2min(orari.mattina?.inizio || "07:00"), pomeriggio: hm2min(orari.pomeriggio?.inizio || "14:00"), notte: hm2min(orari.notte?.inizio || "20:00"), cucina: hm2min(orari.cucina?.inizio || "07:00") };
  const shiftEnd = { mattina: orari.mattina?.fine || "14:00", pomeriggio: orari.pomeriggio?.fine || "20:00", notte: orari.notte?.fine || "07:00", cucina: orari.cucina?.fine || "14:00" };
  const confirmFromLabel = (turno) => { const raw = ((shiftStart[turno] || 0) - 10 + 1440) % 1440; return `${String(Math.floor(raw / 60)).padStart(2, "0")}:${String(raw % 60).padStart(2, "0")}`; };

  const { data: oggi = [] } = useQuery({ queryKey: ["presenze", selectedStructure, selDate], queryFn: async () => (await api.get("/presenze", { params: { data: selDate, structure_id: selectedStructure } })).data, refetchInterval: 30000 });
  const monthStart = `${today.slice(0, 7)}-01`;
  const { data: storico = { records: [], riepilogo: [] } } = useQuery({ queryKey: ["presenze-storico", selectedStructure, monthStart], enabled: tab === "storico", queryFn: async () => (await api.get("/presenze/storico", { params: { structure_id: selectedStructure, start: monthStart, end: today } })).data });

  const { data: turniAll = [] } = useQuery({ queryKey: ["presenze-turni", selectedStructure, today], queryFn: async () => (await api.get("/turni", { params: { structure_id: selectedStructure } })).data, refetchInterval: 30000 });
  const { data: operatoriList = [] } = useQuery({ queryKey: ["presenze-operatori", selectedStructure], queryFn: async () => (await api.get("/firma/operatori", { params: { structure_id: selectedStructure } })).data });
  _useEffectChk(() => {
    const myName = opName || user?.name;
    if ((oggi || []).some((p) => (p.operatore_nome || "") === myName)) _markChk("presenza");
  }, [oggi, opName]); // eslint-disable-line
  const norm = (s) => (s || "").trim().toLowerCase();
  // I turni salvano "nome cognome" mentre le presenze salvano operatore_id: risolvo il nome del turno all'id operatore
  const opIdByName = React.useMemo(() => {
    const m = {};
    (operatoriList || []).forEach((o) => {
      const id = o.id;
      if (!id) return;
      m[norm(`${o.nome || ""} ${o.cognome || ""}`)] = id;
      if (!(norm(o.nome) in m)) m[norm(o.nome)] = id;
    });
    return m;
  }, [operatoriList]);
  const resolveOpId = (name) => opIdByName[norm(name)];
  const todayTurni = turniAll.filter((t) => t.data === selDate);
  const assignedKeys = activeOperatore ? [...new Set(todayTurni.filter((t) => norm(t.operatore_nome) === norm(opName)).flatMap((t) => fasceOf(t.turno)))] : TURNI.map((t) => t.key);
  const turnoOps = (k) => todayTurni.filter((t) => cardMatch(t.turno, k)).map((t) => t.operatore_nome);
  const oggiVisibile = onlySelf ? oggi.filter((p) => p.operatore_id === activeOperatore?.id || norm(p.operatore_nome) === norm(opName)) : oggi;

  const mine = (turno) => oggi.find((p) => p.turno === turno && p.operatore_id === activeOperatore?.id);
  // Conferma consentita al massimo 10 minuti prima dell'inizio del turno (solo per oggi)
  const canConfirm = (turno) => {
    if (!isToday) return false;
    const now = new Date(); const mins = now.getHours() * 60 + now.getMinutes();
    if (turno === "notte") return mins >= shiftStart.notte - 10 || mins < hm2min(shiftEnd.notte);
    return mins >= (shiftStart[turno] ?? 0) - 10;
  };
  // Turno completato quando l'orario di fine è passato (la notte finisce il giorno dopo)
  const turnoCompletato = (turno) => {
    const end = new Date(`${selDate}T00:00:00`);
    const [eh, em] = String(shiftEnd[turno]).split(":").map(Number);
    if (hm2min(shiftEnd[turno]) <= (shiftStart[turno] ?? 0)) end.setDate(end.getDate() + 1);
    end.setHours(eh || 0, em || 0, 0, 0);
    return new Date() >= end;
  };

  const inizia = async (turno) => {
    if (!activeOperatore) { toast.error("Nessun operatore attivo: sblocca con la Firma Rapida"); return; }
    try {
      await api.post("/presenze/inizio", { turno, ora: nowHM(), data: selDate, operatore_id: activeOperatore.id, operatore_nome: opName, structure_id: structForOp });
      toast.success("Ingresso confermato!"); qc.invalidateQueries({ queryKey: ["presenze"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const esci = async (p) => { await api.post("/presenze/fine", { presenza_id: p.id, ora: nowHM() }); toast.success("Uscita anticipata registrata"); qc.invalidateQueries({ queryKey: ["presenze"] }); };

  const structColor = (structures || []).find((s) => s.id === selectedStructure)?.colore || "#1f7a5c";
  const structName = selectedStructure === "all" ? "Tutte le strutture" : ((structures || []).find((s) => s.id === selectedStructure)?.nome || "");
  const presCols = [
    { header: "Data", accessor: "data" },
    { header: "Operatore", accessor: "operatore_nome" },
    { header: "Turno", accessor: "turno" },
    { header: "Ingresso previsto", accessor: "inizio_previsto" },
    { header: "Ingresso effettivo", accessor: "ora_inizio" },
    { header: "Ritardo (min)", accessor: (p) => p.ritardo_min || 0 },
    { header: "Livello", accessor: "ritardo_livello" },
    { header: "Fine prevista", accessor: "fine_prevista" },
    { header: "Uscita effettiva", accessor: (p) => p.ora_fine || "" },
    { header: "Anticipo uscita (min)", accessor: (p) => p.uscita_anticipata_min || 0 },
  ];

  const dateLabel = new Date(selDate).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });

  return (
    <div>
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Clock size={22} /> Presenze</h1>
          <p className="text-sm text-muted-foreground capitalize">{dateLabel}</p>
        </div>
        <div className="flex gap-2">
          <Button variant={tab === "oggi" ? "default" : "outline"} size="sm" onClick={() => setTab("oggi")} data-testid="pres-tab-oggi"><CalendarDays size={15} className="mr-1" /> Oggi</Button>
          {isManager && <Button variant={tab === "storico" ? "default" : "outline"} size="sm" onClick={() => setTab("storico")} data-testid="pres-tab-storico"><History size={15} className="mr-1" /> Storico</Button>}
        </div>
      </div>

      {tab === "oggi" && (
        <>
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-muted-foreground">Giorno:</span>
            <Input type="date" value={selDate} max={today} onChange={(e) => setSelDate(e.target.value || today)} className="w-auto" data-testid="pres-date" />
            {!isToday && <Button size="sm" variant="outline" onClick={() => setSelDate(today)} data-testid="pres-oggi-btn">Torna a oggi</Button>}
          </div>
          {onlySelf ? (
            <div className="mb-6" data-testid="pres-self-view">
              {(() => {
                const myKeys = TURNI.filter((t) => assignedKeys.includes(t.key));
                if (myKeys.length === 0) return <p className="text-sm text-muted-foreground" data-testid="pres-noturno">Nessun turno assegnato a te per {isToday ? "oggi" : "questo giorno"}.</p>;
                return (
                  <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
                    {myKeys.map((t) => {
                      const p = mine(t.key);
                      const liv = p ? (LIV[p.ritardo_livello] || LIV.puntuale) : null;
                      const completato = turnoCompletato(t.key);
                      return (
                        <div key={t.key} className="gr-card p-6" style={{ borderTop: `5px solid ${t.color}` }} data-testid={`pres-self-${t.key}`}>
                          <div className="flex items-center gap-3">
                            <span className="h-12 w-12 rounded-2xl grid place-items-center shrink-0" style={{ background: t.bg, color: t.color }}><t.icon size={24} /></span>
                            <div className="min-w-0">
                              <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Il tuo turno di oggi</div>
                              <div className="font-head font-bold text-xl leading-tight">{t.label}</div>
                              <div className="text-sm text-muted-foreground tabnum">{orarioLabel(orari[t.key]) || "orario non impostato"}</div>
                            </div>
                            {completato && <span className="ml-auto text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid={`pres-self-completato-${t.key}`}>Completato</span>}
                          </div>
                          <div className="mt-5">
                            {p ? (
                              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid={`pres-self-done-${t.key}`}>
                                <div className="flex items-center gap-2 text-emerald-800 font-bold"><CheckCircle2 size={20} /> Presenza confermata alle {p.ora_inizio}</div>
                                <div className="mt-2 flex items-center gap-2">
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${liv[1]}`}>{liv[0]}{p.ritardo_min > 0 ? ` +${p.ritardo_min}′` : ""}</span>
                                  {!p.ora_fine && <button onClick={() => esci(p)} className="text-xs font-semibold text-muted-foreground hover:text-red-600 inline-flex items-center gap-1 ml-auto" data-testid={`pres-self-fine-${t.key}`}><LogOut size={13} /> Esco prima</button>}
                                </div>
                              </div>
                            ) : isToday ? (
                              canConfirm(t.key) ? (
                                <Button onClick={() => inizia(t.key)} data-testid={`pres-self-conferma-${t.key}`} className="w-full py-6 text-lg font-bold rounded-xl text-white" style={{ background: t.color }}>
                                  <CheckCircle2 size={22} className="mr-2" /> Conferma presenza
                                </Button>
                              ) : (
                                <div className="rounded-xl border bg-secondary/40 p-4 text-center" data-testid={`pres-self-presto-${t.key}`}>
                                  <div className="text-sm font-semibold text-slate-500">Potrai confermare dalle <span className="tabnum text-foreground">{confirmFromLabel(t.key)}</span></div>
                                  <div className="text-[11px] text-muted-foreground mt-0.5">(fino a 10 minuti prima dell'inizio del turno)</div>
                                </div>
                              )
                            ) : (
                              <div className="rounded-xl border bg-secondary/40 p-4 text-center text-sm font-semibold text-amber-600" data-testid={`pres-self-nonconf-${t.key}`}>Non confermato</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {!activeOperatore && <p className="text-sm text-amber-600 mt-4">⚠️ Sblocca con la Firma Rapida per confermare il tuo ingresso.</p>}
            </div>
          ) : (<>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {(onlySelf ? TURNI.filter((t) => assignedKeys.includes(t.key)) : TURNI).map((t) => {
              const assigned = todayTurni.filter((x) => cardMatch(x.turno, t.key) && (!onlySelf || norm(x.operatore_nome) === norm(opName)));
              return (
                <div key={t.key} className="gr-card p-5" style={{ borderTop: `4px solid ${t.color}` }} data-testid={`pres-turno-${t.key}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: t.bg, color: t.color }}><t.icon size={18} /></span>
                    <div><div className="font-head font-bold leading-tight">{t.label}</div><div className="text-xs text-muted-foreground">{orarioLabel(orari[t.key])}</div></div>
                    {turnoCompletato(t.key) && <span className="ml-auto text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid={`pres-completato-${t.key}`}>Turno completato</span>}
                  </div>
                  {assigned.length === 0 ? <p className="text-xs text-muted-foreground py-2">Nessun operatore assegnato</p> : (
                    <div className="space-y-2">
                      {assigned.map((a) => {
                        const aid = a.operatore_id || resolveOpId(a.operatore_nome);
                        const p = oggi.find((x) => x.turno === t.key && (aid ? x.operatore_id === aid : norm(x.operatore_nome) === norm(a.operatore_nome)));
                        const isMe = !!activeOperatore && (aid ? aid === activeOperatore.id : norm(a.operatore_nome) === norm(opName));
                        const liv = p ? (LIV[p.ritardo_livello] || LIV.puntuale) : null;
                        return (
                          <div key={a.id} className={`flex items-center gap-2 p-2 rounded-lg border text-sm ${isMe ? "border-primary bg-primary/5" : "bg-white"}`} data-testid={`pres-op-${t.key}-${a.operatore_id || a.id}`}>
                            <span className="flex-1 font-medium truncate">{a.operatore_nome}{isMe && <span className="text-[10px] text-primary font-bold ml-1">(tu)</span>}</span>
                            {p ? (
                              <>
                                <CheckCircle2 size={14} className="text-emerald-600" />
                                <span className="tabnum">{p.ora_inizio}</span>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${liv[1]}`}>{p.ritardo_min > 0 ? `+${p.ritardo_min}′` : "ok"}</span>
                                {isMe && !p.ora_fine && <button onClick={() => esci(p)} title="Esco prima" className="text-muted-foreground hover:text-red-600" data-testid={`pres-fine-${t.key}`}><LogOut size={14} /></button>}
                              </>
                            ) : isMe && isToday ? (
                              canConfirm(t.key) ? (
                                <Button size="sm" style={{ background: t.color }} onClick={() => inizia(t.key)} data-testid={`pres-inizia-${t.key}`}>Conferma</Button>
                              ) : (
                                <span className="text-[11px] font-semibold text-slate-400 text-right leading-tight" data-testid={`pres-presto-${t.key}`} title="Puoi confermare al massimo 10 minuti prima dell'inizio del turno">Confermabile<br />dalle {confirmFromLabel(t.key)}</span>
                              )
                            ) : (
                              <span className="text-[11px] font-semibold text-amber-600" data-testid={`pres-attesa-${t.key}`}>{isToday ? "In attesa" : "Non confermato"}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {onlySelf && !TURNI.some((t) => assignedKeys.includes(t.key)) && <p className="text-sm text-muted-foreground mb-4" data-testid="pres-noturno">Nessun turno assegnato a te per {isToday ? "oggi" : "questo giorno"}.</p>}
          {!activeOperatore && <p className="text-sm text-amber-600 mb-4">⚠️ Sblocca con la Firma Rapida per confermare il tuo ingresso.</p>}

          <div className="gr-card p-4">
            <h2 className="font-head font-bold mb-3">{onlySelf ? "I miei ingressi di oggi" : "Ingressi di oggi"} ({oggiVisibile.length})</h2>
            {oggiVisibile.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Nessun ingresso confermato oggi.</p> : (
              <div className="space-y-2">
                {oggiVisibile.map((p) => { const liv = LIV[p.ritardo_livello] || LIV.puntuale; return (
                  <div key={p.id} className="flex items-center gap-3 p-2.5 rounded-lg border bg-white text-sm" data-testid={`pres-row-${p.id}`}>
                    <span className="font-semibold flex-1">{p.operatore_nome}</span>
                    <span className="capitalize text-muted-foreground">{p.turno}</span>
                    <span className="tabnum">{p.ora_inizio}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${liv[1]}`}>{liv[0]}{p.ritardo_min > 0 ? ` +${p.ritardo_min}′` : ""}</span>
                  </div>
                ); })}
              </div>
            )}
          </div>
          </>)}
        </>
      )}

      {tab === "storico" && (
        <div className="space-y-6">
          <div className="gr-card p-4">
            <h2 className="font-head font-bold mb-3">Riepilogo ritardi del mese <span className="text-xs font-normal text-muted-foreground">(oltre 3 ritardi = ricorrente)</span></h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {storico.riepilogo.map((r) => { const rec = r.ritardi > 3; return (
                <div key={r.operatore} className={`rounded-lg border p-3 ${rec ? "border-red-400 bg-red-50" : ""}`} data-testid={`pres-riep-${r.operatore}`}>
                  <div className="flex items-center gap-2"><span className="font-semibold">{r.operatore}</span>{rec && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white" data-testid={`pres-ricorrente-${r.operatore}`}>Ritardi ricorrenti</span>}</div>
                  <div className="text-xs text-muted-foreground mt-1">{r.turni} turni · {r.ritardi} ritardi · {r.uscite_anticipate} uscite anticipate</div>
                  <div className={`text-lg font-head font-bold tabnum mt-1 ${rec ? "text-red-700" : "text-red-600"}`}>{r.min_ritardo}′ <span className="text-xs font-normal text-muted-foreground">ritardo totale</span></div>
                </div>
              ); })}
              {storico.riepilogo.length === 0 && <p className="text-sm text-muted-foreground">Nessun dato.</p>}
            </div>
          </div>
          <div className="gr-card p-4 overflow-x-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-head font-bold">Storico ingressi</h2>
              <ExportButton filename="presenze" title="Storico presenze" subtitle={structName} columns={presCols} rows={storico.records || []} color={structColor} testid="pres-export" />
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b"><th className="p-2">Data</th><th className="p-2">Operatore</th><th className="p-2">Turno</th><th className="p-2">Ingresso</th><th className="p-2">Ritardo</th><th className="p-2">Uscita</th></tr></thead>
              <tbody>
                {storico.records.map((p) => { const liv = LIV[p.ritardo_livello] || LIV.puntuale; return (
                  <tr key={p.id} className="border-b" data-testid={`pres-hist-${p.id}`}>
                    <td className="p-2">{p.data}</td><td className="p-2 font-semibold">{p.operatore_nome}</td>
                    <td className="p-2 capitalize">{p.turno}</td><td className="p-2 tabnum">{p.ora_inizio}</td>
                    <td className="p-2"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${liv[1]}`}>{p.ritardo_min > 0 ? `+${p.ritardo_min}′` : "—"}</span></td>
                    <td className="p-2 tabnum">{p.ora_fine || "regolare"}{p.uscita_anticipata_min > 0 ? ` (-${p.uscita_anticipata_min}′)` : ""}</td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
